import { ReactNode } from 'react'
import { Box, CircularProgress, SxProps, Theme, Tooltip, Typography } from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'

import { CardBox } from '../../../../theme/styleComponents'
import { CHART_HEIGHT } from './constants'

interface ChartCardProps {
  title: string
  subtitle?: string
  /** Overrides for the subtitle's text style. */
  subtitleSx?: SxProps<Theme>
  tooltip?: string
  loading?: boolean
  isEmpty?: boolean
  emptyMessage?: string
  height?: number
  children: ReactNode
}

/**
 * Frame shared by every chart on the Charts tab, so each one gets the same header, loading and
 * empty treatment and a fixed plot height (ResponsiveContainer needs a sized parent).
 */
const ChartCard = ({
  title,
  subtitle,
  subtitleSx,
  tooltip,
  loading,
  isEmpty,
  emptyMessage = 'No data available for this chart yet.',
  height = CHART_HEIGHT,
  children
}: ChartCardProps) => (
  <CardBox height="100%">
    <Box px={3} py={2} borderBottom="1px solid" borderColor="lightGrayThree">
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography fontSize="18px">{title}</Typography>
        {tooltip && (
          <Tooltip title={tooltip}>
            <InfoOutlined fontSize="small" color="action" />
          </Tooltip>
        )}
      </Box>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={subtitleSx}>
          {subtitle}
        </Typography>
      )}
    </Box>

    <Box p={2} height={height} display="flex" alignItems="center" justifyContent="center">
      {loading ? (
        <CircularProgress size={28} />
      ) : isEmpty ? (
        <Typography color="text.secondary">{emptyMessage}</Typography>
      ) : (
        <Box width="100%" height="100%">
          {children}
        </Box>
      )}
    </Box>
  </CardBox>
)

export default ChartCard
